import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  makeStyles,
} from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';

import {
  Migration,
  MigrationDevice,
  addDevices,
  getMigrations,
  removeDevices,
} from '../api/migrations';
import BulkActionResultView, {
  DeviceActionResult,
  DeviceActionResultRow,
} from './BulkActionResultView';
import { errorText } from '../lib/errorText';
import { migrationTypeLabel } from '../lib/vocabulary';

import EnvironmentEcho from './EnvironmentEcho';
import { IDENTIFIER } from '../theme/tokens';
// FD-372 — "Move to migration…": the H2 retry path an operator actually walks.
// A device that salvaged/faulted cannot retry inside its wave (one package per
// migration for its whole life, FD-ADR-034 D3); its only path forward is
// another campaign. The move is remove-from-old THEN add-to-new, per device,
// SEQUENTIAL — the same "N individually audited, resumable operations" shape
// FD-ADR-030 §5 chose over a cascade, and for the same reason: the failure
// mode of a batched move is a partial state with no record of how far it got.
//
// Every device therefore gets its OWN result row, and the ugliest partial —
// removed from the old campaign but NOT added to the new one — is rendered as
// exactly that, in red, with the remedy. Hiding it inside a rollup would be
// the one dishonest rendering available here.
//
// NO business logic beyond the remove→add chain lives client-side (the task's
// boundary): each step is the existing audited endpoint, and the server stays
// the authority on what each accepts.

export interface MoveDevicesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  migrationId: string;
  migrationName: string;
  // Devices already selected on the parent's DeviceGrid — a read-only receipt,
  // not editable here (the ConfirmRemoveDevicesModal precedent).
  selectedDevices: MigrationDevice[];
  // Fires on close if at least one device changed campaign state (any remove
  // succeeded — including the partial where the add then failed, because the
  // source grid is stale either way). Parent invalidates + clears selection.
  onMoved?: () => void;
  /**
   * TEST-ONLY: pre-select a target campaign so the move flow can be exercised
   * without driving the Fluent Dropdown's portal. There is no CI-proven idiom
   * in this repo for opening a Fluent listbox under jsdom — GroupPicker.test
   * (the only other Dropdown suite) deliberately never opens it — and the
   * interaction is genuinely environment-fragile: Fluent's trigger closes the
   * popup on blur (useTriggerSlot), and this dialog's deferred focus work can
   * land AFTER the open gesture on a loaded runner, closing the listbox the
   * instant it opened. Reproduced locally under CPU contention for BOTH
   * pointer and keyboard opens. Never set from application code.
   */
  initialTargetId?: string;
}

/** One pickable target campaign, labelled in OPERATOR vocabulary. */
export interface MoveTargetOption {
  migrationId: string;
  label: string;
}

/**
 * Derives the target-picker options: every campaign EXCEPT the source (a move
 * to itself is a no-op wearing a button), labelled Hybrid/Cloud — the wire's
 * D2C/T2T never reaches the operator (FD-ADR-031 §3). Exported so the
 * exclusion and the vocabulary are unit-testable without portal interaction;
 * the Dropdown below maps over exactly this, so the test and the UI cannot
 * disagree.
 */
export function buildMoveTargetOptions(
  migrations: Migration[],
  sourceMigrationId: string,
): MoveTargetOption[] {
  return migrations
    .filter((m) => m.migrationId !== sourceMigrationId)
    .map((m) => ({
      migrationId: m.migrationId,
      label: `${m.migrationName} — ${migrationTypeLabel(m.migrationType)} · ${m.migrationWave}`,
    }));
}

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '16px',
    minWidth: '520px',
  },
  description: {
    fontSize: '13px',
    color: '#52525b',
  },
  serials: {
    maxHeight: '120px',
    overflowY: 'auto',
    ...IDENTIFIER,
    fontSize: '12px',
    lineHeight: '1.6',
  },
  progressLine: {
    fontSize: '12px',
    color: '#52525b',
    ...IDENTIFIER,
  },
});

// Page the migrations list to completion, bounded. The picker needs the full
// campaign set (a target beyond page one would otherwise be silently
// unofferable), and campaigns number tens-to-hundreds (FD-ADR-030 §2), so a
// bounded loop is proportionate. The bound exists so a paging defect can never
// turn this into an unbounded loop.
const MAX_PICKER_PAGES = 5;

async function getAllMigrationsForPicker(): Promise<Migration[]> {
  const all: Migration[] = [];
  let token: string | null = null;
  for (let page = 0; page < MAX_PICKER_PAGES; page++) {
    const res = await getMigrations({ pageSize: 100, continuationToken: token });
    all.push(...res.migrations);
    token = res.continuationToken;
    if (!token) break;
  }
  return all;
}

export default function MoveDevicesDialog({
  open,
  onOpenChange,
  migrationId,
  migrationName,
  selectedDevices,
  onMoved,
  initialTargetId,
}: MoveDevicesDialogProps) {
  const styles = useStyles();
  const [targetId, setTargetId] = useState<string | null>(initialTargetId ?? null);
  const [isRunning, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [rows, setRows] = useState<DeviceActionResultRow[] | null>(null);
  const [isDone, setDone] = useState(false);

  // Reset every piece of local state when the dialog closes, so reopening
  // never shows a previous run's result or a stale target.
  useEffect(() => {
    if (!open) {
      setTargetId(initialTargetId ?? null);
      setRunning(false);
      setProgress(0);
      setRows(null);
      setDone(false);
    }
  }, [open, initialTargetId]);

  // Fetched only while the dialog is open — the page must not pay for the
  // picker it has not asked for. Deliberately NOT filtered on Migration.status:
  // that column is written 'active' at creation and never updated (FD-269), so
  // filtering on it would be a filter that cannot discriminate. The one row
  // excluded is the source campaign — a move to itself is a no-op wearing a
  // button.
  const migrationsQuery = useQuery({
    queryKey: ['migrations', 'move-picker'],
    queryFn: getAllMigrationsForPicker,
    enabled: open,
  });
  const targetOptions = buildMoveTargetOptions(migrationsQuery.data ?? [], migrationId);
  const target = targetOptions.find((o) => o.migrationId === targetId);

  const count = selectedDevices.length;
  const serials = selectedDevices.map((d) => d.serialNumber);

  async function moveOne(serial: string, toId: string): Promise<DeviceActionResultRow> {
    // Step 1 — remove from the source campaign. A failure here leaves the
    // device exactly where it was: unchanged, resumable, and said so.
    try {
      const rem = await removeDevices(migrationId, [serial]);
      if (!(rem.data.removed ?? []).includes(serial)) {
        const notFound = (rem.data.notFound ?? []).includes(serial);
        return {
          serialNumber: serial,
          result: 'Not moved',
          detail: notFound
            ? 'Not found on this campaign — nothing was changed.'
            : (rem.message ?? 'The source campaign did not remove it — nothing was changed.'),
          intent: 'warning',
        };
      }
    } catch (err) {
      return {
        serialNumber: serial,
        result: 'Not moved',
        detail: `Remove failed — the device is unchanged on this campaign. ${errorText(err)}`,
        intent: 'danger',
      };
    }

    // Step 2 — add to the target. THIS is the partial that must be visible as
    // exactly what it is: the remove already succeeded, so a failure here
    // leaves the device on NEITHER campaign.
    try {
      const add = await addDevices(toId, [serial]);
      if ((add.data.added ?? []).includes(serial)) {
        const mintFailure = (add.data.mintFailures ?? []).find(
          (f) => f.serialNumber === serial,
        );
        if (mintFailure) {
          return {
            serialNumber: serial,
            result: 'Moved — cannot boot yet',
            detail: `On the new campaign, but ATC MINT registration failed (${mintFailure.reason}). Re-add it to re-register.`,
            intent: 'warning',
          };
        }
        return { serialNumber: serial, result: 'Moved', detail: null, intent: 'success' };
      }
      const rejection =
        (add.data.rejected ?? []).find((r) => r.serialNumber === serial)?.reason ??
        (add.data.indexFailures ?? []).find((r) => r.serialNumber === serial)?.reason ??
        add.message ??
        'the target campaign rejected it';
      return {
        serialNumber: serial,
        result: 'Removed but NOT added',
        detail: `The device is now on neither campaign (${rejection}). Re-add it to a campaign manually.`,
        intent: 'danger',
      };
    } catch (err) {
      return {
        serialNumber: serial,
        result: 'Removed but NOT added',
        detail: `The device is now on neither campaign — re-add it to a campaign manually. ${errorText(err)}`,
        intent: 'danger',
      };
    }
  }

  async function run() {
    if (!targetId || isRunning || count === 0) return;
    setRunning(true);
    setRows([]);
    // Sequential by design — each device is one audited remove and one audited
    // add, and the rows land as they complete so a long run is never a
    // spinner: however far it got is on screen.
    const acc: DeviceActionResultRow[] = [];
    for (let i = 0; i < serials.length; i++) {
      setProgress(i + 1);
      const row = await moveOne(serials[i], targetId);
      acc.push(row);
      setRows([...acc]);
    }
    setRunning(false);
    setDone(true);
  }

  function handleClose() {
    if (isRunning) return; // never abandon a run mid-device
    const anyRemoved =
      rows?.some((r) => r.intent === 'success' || r.result === 'Removed but NOT added') ??
      false;
    onOpenChange(false);
    if (anyRemoved) onMoved?.();
  }

  const result: DeviceActionResult | null = rows
    ? {
        rows,
        summaryMessage: isDone
          ? `${rows.filter((r) => r.intent === 'success').length} of ${count} device${
              count === 1 ? '' : 's'
            } moved to ${target?.label ?? 'the target campaign'}.`
          : null,
      }
    : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) handleClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {isDone
              ? 'Move complete'
              : `Move ${count} device${count === 1 ? '' : 's'} to another campaign`}
          </DialogTitle>
          <DialogContent>
            <div className={styles.body}>
              {/* FD-422 — a move is remove + add per device (FD-ADR-035 §3), both
                  of which reach ATC MINT. It lands entirely within one
                  deployment; naming it is what makes that visible. */}
              <EnvironmentEcho />
              {result ? (
                <>
                  {isRunning && (
                    <div className={styles.progressLine} role="status">
                      Moving device {progress} of {count}…
                    </div>
                  )}
                  <BulkActionResultView result={result} />
                </>
              ) : (
                <>
                  <div className={styles.description}>
                    Each device is removed from <strong>{migrationName}</strong>, then added
                    to the campaign you pick — one device at a time, each step recorded in
                    the event trail.
                  </div>

                  <MessageBar intent="warning">
                    <MessageBarBody>
                      <MessageBarTitle>Identity mappings do not move</MessageBarTitle>
                      A moved device waits at the identity gate on the new campaign until its
                      user is mapped there again, and its readiness is re-evaluated fresh
                      from its next manifest.
                    </MessageBarBody>
                  </MessageBar>

                  <Field label="Target campaign" hint="The campaign these devices move to.">
                    <Dropdown
                      placeholder={
                        migrationsQuery.isLoading
                          ? 'Loading campaigns…'
                          : targetOptions.length === 0
                            ? 'No other campaign exists'
                            : 'Pick a campaign'
                      }
                      value={target?.label ?? ''}
                      selectedOptions={targetId ? [targetId] : []}
                      disabled={migrationsQuery.isLoading || targetOptions.length === 0}
                      onOptionSelect={(_, data) => setTargetId(data.optionValue ?? null)}
                    >
                      {targetOptions.map((o) => (
                        <Option key={o.migrationId} value={o.migrationId} text={o.label}>
                          {o.label}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>

                  {migrationsQuery.isError && (
                    <MessageBar intent="error">
                      <MessageBarBody>
                        <MessageBarTitle>Could not load campaigns</MessageBarTitle>
                        {errorText(migrationsQuery.error)}
                      </MessageBarBody>
                    </MessageBar>
                  )}

                  <div className={styles.serials}>
                    {serials.map((s) => (
                      <div key={s}>{s}</div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            {isDone ? (
              <Button appearance="primary" onClick={handleClose}>
                Close
              </Button>
            ) : (
              <>
                <Button appearance="subtle" onClick={handleClose} disabled={isRunning}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  onClick={run}
                  disabled={!targetId || count === 0 || isRunning}
                >
                  {isRunning
                    ? `Moving ${progress} of ${count}…`
                    : `Move ${count} device${count === 1 ? '' : 's'}`}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
